import { c as _c } from "react/compiler-runtime";
import { Sidebar } from "@components/sidebar/sidebar";
import { SplashScreenManager } from "@components/splash-screen/splash-screen-manager";
import { useDocumentTitle } from "@hooks/use-document-title";
import { useErrorHandler } from "@hooks/use-error-handler";
import { useHandleWindowClose } from "@hooks/use-handle-window-close";
import { SdbCompanyLoader } from "@loaders/sdb-company-loader";
import { useInitAppAnalytics } from "@hooks/use-init-app-analytics";
import { Box } from "@mui-wrapper";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import { LicenseInfo, muiXTelemetrySettings } from "@mui/x-license";
import { runtimeConfig } from "@src/runtime-config";
import { useAppSelector } from "@store/store-helper";
import { sidebarSelector } from "@store/ui/ui-selector";
import { Outlet } from "react-router-dom";

// MUI X sends a telemetry event on every license verification. In dev builds that means a
// request to x-telemetry.mui.com carrying a device fingerprint, so opt out globally before any
// licensed component mounts. Production builds already compile the sender out via NODE_ENV.
muiXTelemetrySettings.disableTelemetry();

// TODO: remove later, this is for testing purposes https://faro01.atlassian.net/browse/ST-4410
// eslint-disable-next-line no-console
console.log("App component rendered 214ea097-11c8-4114-b850-70cc14ccf982");

/**
 * All the context providers should be added here to make them available only for those pages which are using app layout.
 *
 * @returns all the react context providers and it's initialization.
 */
function AllAppLayoutContextProviders(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <LocalizationProvider dateAdapter={AdapterLuxon}><SdbCompanyLoader>{children}</SdbCompanyLoader></LocalizationProvider>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Entry point deciding on the structure of the app
 */
export function App() {
  const $ = _c(7);
  if (runtimeConfig.features.muiProKey) {
    LicenseInfo.setLicenseKey(runtimeConfig.features.muiProKey);
  }
  const sidebar = useAppSelector(sidebarSelector);
  useErrorHandler();
  useHandleWindowClose();
  useDocumentTitle();
  useInitAppAnalytics();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      display: "flex",
      height: "100vh"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== sidebar.isVisible) {
    t1 = sidebar.isVisible && <Sidebar />;
    $[1] = sidebar.isVisible;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Outlet />;
    t3 = <SplashScreenManager />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1) {
    t4 = <AllAppLayoutContextProviders><div className="App" data-testid="app-container"><Box component="div" sx={t0}>{t1}{t2}{t3}</Box></div></AllAppLayoutContextProviders>;
    $[5] = t1;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}